import flatten from 'lodash/flatten'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'
import Papa from 'papaparse'

const parserConfig = {
  dynamicTyping: true,
  skipEmptyLines: true,
} as const

/**
 * Parses a CSV file into an array of data rows.
 *
 * @param file - CSV file to parse
 * @returns Promise resolving to array of rows
 *
 * @example
 * ```typescript
 * const rows = await parseCsv(file);
 * ```
 */
const parseCsv = (file: File): Promise<any> => {
  return new Promise(
    (
      resolve: (result: Promise<never>) => void,
      reject: (error?: any) => void,
    ) => {
      return Papa.parse(file, {
        parserConfig,
        complete: results => resolve(results.data),
        error: error => reject(error.errors),
      })
    },
  )
}

export default parseCsv

// TODO: type this
type Row = any
type Column = any

/**
 * Converts row data into a list of column objects.
 *
 * @param rows - CSV rows
 * @returns Array of columns or null when no data
 *
 * @example
 * ```typescript
 * const columns = extractColumns(rows);
 * ```
 */
export const extractColumns = (rows: Row[]): Column[] | null | undefined => {
  if (!rows || rows.length === 0) return null

  // Filter out null/undefined rows and find max column count
  const validRows = rows.filter(row => row && Array.isArray(row))
  if (validRows.length === 0) return null

  const maxColumns = Math.max(...validRows.map(row => row.length))
  const columns: Array<Column> = []

  for (let i = 0; i < maxColumns; i++) {
    columns.push({ name: `Column ${i + 1}`, rows: [] })
  }

  rows.forEach(row => {
    if (row && Array.isArray(row) && row.length > 0) {
      for (let i = 0; i < columns.length; i++) {
        columns[i].rows.push(i < row.length ? row[i] : undefined)
      }
    } else {
      // Handle null/undefined/empty rows by adding null to each column
      columns.forEach(col => col.rows.push(null))
    }
  })

  return columns
}

const headerMatch = [
  'phone',
  'email',
  'full name',
  'first name',
  'last name',
  'name',
  'country',
  'territory',
  'location',
  'company',
  'organisation',
]

const headerMapping = {
  'First Name': {
    words: ['first name'],
    partialMatch: false,
  },
  'Last Name': {
    words: ['last name'],
    partialMatch: false,
  },
  Name: {
    words: ['name', 'full name'],
    partialMatch: false,
  },
  Email: {
    words: ['email'],
    partialMatch: true,
  },
  Phone: {
    words: ['phone'],
    partialMatch: true,
  },
  'Company/Organization': {
    words: ['company', 'organization', 'company'],
    partialMatch: true,
  },
  Location: {
    words: [
      'territory',
      'country',
      'territory/country',
      'region',
      'city',
      'location',
    ],
    partialMatch: true,
  },
  Tags: {
    words: ['type', 'tags'],
    partialMatch: true,
  },
} as const

const apiMapping = {
  Name: 'name',
  Email: 'email',
  Phone: 'phone',
  'Company/Organization': 'company',
  Location: 'territory',
  Tags: 'tags',
} as const

const requiredMappingFields = ['Email', ['First Name', 'Last Name', 'Name']]

/**
 * Validates that a mapping contains the required contact fields.
 *
 * @param mapping - Column mapping object
 * @returns True when mapping is complete
 *
 * @example
 * ```typescript
 * if (verifyContactsMapping(mapping)) {
 *   // proceed
 * }
 * ```
 */
export const verifyContactsMapping = (mapping: any): boolean => {
  if (!mapping || typeof mapping !== 'object') {
    return false
  }
  const requiredFiedldsSet = !requiredMappingFields.some(f => {
    return !Object.keys(mapping).find(k => {
      if (Array.isArray(f)) {
        return f.includes(mapping[k])
      }
      return mapping[k] === f
    })
  })
  return requiredFiedldsSet
}

const mapHeaderRow = (headerRow: Row) => {
  const mapping: Record<string, any> = {}
  let headerMappingSource = headerMapping as any
  headerRow.forEach((value, index) => {
    const key = Object.keys(headerMappingSource).find(key => {
      const { words, partialMatch } = headerMappingSource[key]
      return (
        value &&
        words.some(w => {
          const v = value.toLowerCase().trim()
          return w === v || (partialMatch && v.indexOf(w) > -1)
        })
      )
    })
    if (key) {
      mapping[index] = key
      // prevent multiple matches
      if (key !== 'Tags') headerMappingSource = omit(headerMappingSource, key)
    }
  })
  return mapping
}

/**
 * Processes CSV rows into structured contacts data.
 *
 * @param rows - CSV rows
 * @returns Object containing header, mapping, columns and row mappings
 *
 * @example
 * ```typescript
 * const contactsData = parseContactsCsv(rows);
 * ```
 */
export const parseContactsCsv = (rows: Row[]): any => {
  let headerRowSimilarity = 0
  if (rows.length > 0) {
    const matchArray = headerMatch.slice()
    rows[0].forEach(column => {
      const index = matchArray.findIndex(
        word => column && column.toLowerCase().includes(word),
      )
      if (column && index > -1) {
        headerRowSimilarity++
        matchArray.splice(index, 1)
      }
    })
  }

  const headerRow = headerRowSimilarity >= 1
  const rowsMapping: Record<string, any> = {}
  const sourceRows: Array<Row> = []

  const filteredRows = headerRow ? rows.slice(1) : rows

  filteredRows.forEach((row, rowIndex) => {
    const rowHasData = row && row.some(c => c && c.toString().trim().length > 0)
    if (rowHasData) {
      sourceRows.push(row)
      rowsMapping[sourceRows.length - 1] = {
        originalRowIndex: rowIndex + 1 + (headerRow ? 1 : 0),
        originalData: row,
      }
    }
  })

  const sourceColumns = extractColumns(sourceRows)

  const mapping = headerRow ? mapHeaderRow(rows[0]) : {}
  return {
    header: headerRow ? rows[0] : [],
    mapping: mapping,
    columns: sourceColumns,
    rowsMapping,
  }
}

const getName = (row: {
  custom_data?: never
  tags?: Array<never>
  [key: string]: any
}) => {
  if (row.name) return row

  const newRow = { ...row }
  if (newRow.custom_data) {
    let name
    if (newRow.custom_data['First Name']) {
      name = newRow.custom_data['First Name']
      delete newRow.custom_data['First Name']
    }
    if (newRow.custom_data['Last Name']) {
      name = `${name || ''} ${newRow.custom_data['Last Name']}`
      delete newRow.custom_data['Last Name']
    }
    if (name) {
      newRow.name = name.trim()
    }
  }

  return newRow
}

/**
 * Converts mapped column data to contact objects ready for API import.
 *
 * @param mappedColumns - Result of parseContactsCsv containing mapping & columns
 * @returns Array of contact objects
 *
 * @example
 * ```typescript
 * const contacts = exportMappedResults(mapped);
 * ```
 */
export const exportMappedResults = (mappedColumns: any): any => {
  const { mapping, columns } = mappedColumns
  const apiColumns: Record<string, any> = {}
  const customColumns: Record<string, any> = {}
  Object.keys(mapping).forEach(key => {
    const apiKey = apiMapping[mapping[key]]
    if (apiKey) {
      apiColumns[apiKey] = [...(apiColumns[apiKey] || []), key]
    } else {
      customColumns[mapping[key]] = [...(customColumns[key] || []), key]
    }
  })

  const results: Array<
    | any
    | {
        custom_data: never
        tags: Array<never>
      }
  > = []
  for (let i = 0; i < columns[0].rows.length; i++) {
    let resultRow: Record<string, any> = {}
    Object.keys(apiColumns).forEach(key => {
      if (key === 'tags') {
        const columnValues = [
          ...apiColumns[key].map(column =>
            columns[column].rows[i]
              .split(',')
              .map(t => t.trim())
              // business tag is not allowed and only can be set by backend
              .filter(t => t.toLowerCase() !== 'business'),
          ),
        ]

        resultRow[key] = uniq(flatten(columnValues).filter(t => t !== ''))
      } else resultRow[key] = (columns[apiColumns[key][0]].rows[i] || '').trim()
    })

    Object.keys(customColumns).forEach(
      key =>
        (resultRow['custom_data'] = {
          ...resultRow['custom_data'],
          [key]: (columns[customColumns[key][0]].rows[i] || '').trim(),
        }),
    )

    resultRow = getName(resultRow)
    results.push(resultRow)
  }

  return results
}
